import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DokterLayout from './DokterLayout';
import axiosInstance from '../../api/axios';
import Swal from 'sweetalert2';

const SATUAN    = ['Tablet', 'Kapsul', 'Ml', 'Mg', 'Sachet', 'Tetes', 'Ampul', 'Botol'];
const FREKUENSI = ['1x sehari', '2x sehari', '3x sehari', 'Setiap 4 jam', 'Setiap 6 jam', 'Setiap 8 jam', 'Jika perlu (prn)'];
const WAKTU     = ['Sebelum makan', 'Sesudah makan', 'Saat makan', 'Sebelum tidur', 'Pagi hari', 'Malam hari'];

const emptyResep = () => ({
  id: Date.now() + Math.random(), nama_obat: '', dosis: '', satuan: 'Tablet',
  frekuensi: '3x sehari', waktu: 'Sesudah makan', durasi: '', catatan: '',
});

const formatTekananDarahInput = (value) => {
  const digits = String(value).replace(/\D/g, '').slice(0, 5);
  if (digits.length <= 3) return digits;
  return `${digits.slice(0, 3)}/${digits.slice(3)}`;
};

const formatNadiInput = (value) => String(value).replace(/\D/g, '').slice(0, 3);

const formatSuhuInput = (value) => {
  const digits = String(value).replace(/\D/g, '').slice(0, 3);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}.${digits.slice(2)}`;
};

const formatVitalInput = (key, value) => {
  if (key === 'tekanan_darah') return formatTekananDarahInput(value);
  if (key === 'nadi') return formatNadiInput(value);
  if (key === 'suhu') return formatSuhuInput(value);
  return value;
};

const formatPasienId = (id, kode) => {
  if (kode) return kode;
  if (!id) return 'PSN-0000';
  return `PSN-${String(id).padStart(4, '0')}`;
};

// Helper untuk mengekstrak vital signs dari string pemeriksaan_fisik
const parseVitalsFromPemeriksaanFisik = (text) => {
  if (!text) return { td: '', nadi: '', suhu: '', pemeriksaan: '' };

  const tdMatch   = text.match(/Tekanan Darah:\s*([^\n]+)/);
  const nadiMatch = text.match(/Nadi:\s*([^\n]+)/);
  const suhuMatch = text.match(/Suhu:\s*([^\n]+)/);

  // Membersihkan bagian block VITAL SIGNS dari teks pemeriksaan fisik
  let cleanPemeriksaan = text
    .replace(/=== VITAL SIGNS ===[\s\S]*?(\n\n|$)/, '')
    .trim();

  return {
    td: tdMatch ? tdMatch[1].trim() : '',
    nadi: nadiMatch ? nadiMatch[1].trim() : '',
    suhu: suhuMatch ? suhuMatch[1].trim() : '',
    pemeriksaan: cleanPemeriksaan,
  };
};

export default function RekamMedis() {
  const { pendaftaran_id } = useParams();
  const navigate           = useNavigate();

  const [pendaftaran, setPendaftaran] = useState(null);
  const [existing,    setExisting]    = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [tab,         setTab]         = useState('pemeriksaan');

  const [form, setForm] = useState({
    keluhan_utama: '', anamnesis: '', pemeriksaan_fisik: '',
    hasil_laboratorium: '', catatan_dokter: '', tindakan_lain: '',
    tekanan_darah: '', nadi: '', suhu: '',
    respirasi: '', berat_badan: '', tinggi_badan: '',
  });

  const [resepList, setResepList] = useState([emptyResep()]);

  const [showHistoryModal, setShowHistoryModal]     = useState(false);
  const [patientHistory, setPatientHistory]         = useState([]);
  const [loadingHistory, setLoadingHistory]         = useState(false);
  const [selectedHistoryRm, setSelectedHistoryRm]   = useState(null);

  useEffect(() => {
    const init = async () => {
      try {
        const rmRes  = await axiosInstance.get(`/dokter/rekam-medis/pendaftaran/${pendaftaran_id}`);
        const rmData = rmRes.data?.data;
        setExisting(rmData);

        if (rmData) {
          const vitals = parseVitalsFromPemeriksaanFisik(rmData.pemeriksaan_fisik);

          setForm({
            keluhan_utama:      rmData.keluhan_utama      || '',
            anamnesis:          rmData.anamnesis           || '',
            pemeriksaan_fisik:  vitals.pemeriksaan,
            hasil_laboratorium: rmData.hasil_laboratorium || '',
            catatan_dokter:     rmData.catatan_dokter     || '',
            tindakan_lain:      rmData.tindakan           || '',
            tekanan_darah:      vitals.td,
            nadi:               vitals.nadi,
            suhu:               vitals.suhu,
            respirasi: '', berat_badan: '', tinggi_badan: '',
          });

          if (rmData.resep_parsed?.length) {
            setResepList(rmData.resep_parsed.map((r, i) => ({ ...emptyResep(), ...r, id: i })));
          } else if (rmData.resep) {
            try {
              const parsed = JSON.parse(rmData.resep);
              if (Array.isArray(parsed) && parsed.length) {
                setResepList(parsed.map((r, i) => ({ ...emptyResep(), ...r, id: i })));
              }
            } catch (_) {}
          }
        }

        try {
          const antrianRes  = await axiosInstance.get('/dokter/antrian');
          const antrianList = antrianRes.data?.data ?? [];
          const pend = antrianList.find((a) => String(a.id) === String(pendaftaran_id));
          if (pend) {
            setPendaftaran(pend);
          } else if (rmData?.pendaftaran) {
            setPendaftaran(rmData.pendaftaran);
          }
        } catch (_) {
          if (rmData) {
            setPendaftaran({
              no_antrian: rmData.pendaftaran?.no_antrian ?? '-',
              keluhan:    rmData.keluhan_utama ?? '-',
              pasien:     rmData.pasien,
            });
          }
        }

      } catch (err) {
        console.error('Error loading rekam medis:', err);
      } finally {
        setLoading(false);
      }
    };

    if (pendaftaran_id) init();
  }, [pendaftaran_id]);

  const fetchPatientHistory = async () => {
    const pasienId = pendaftaran?.pasien?.id || pendaftaran?.pasien_id;
    if (!pasienId) return;

    setShowHistoryModal(true);
    setLoadingHistory(true);
    try {
      const res = await axiosInstance.get('/dokter/rekam-medis?per_page=500');
      const allRecords = res.data?.data ?? res.data ?? [];
      const history = allRecords.filter(
        (r) => (r.pasien_id === pasienId || r.pasien?.id === pasienId) && String(r.pendaftaran_id) !== String(pendaftaran_id)
      );
      setPatientHistory(history);
      if (history.length > 0) setSelectedHistoryRm(history[0]);
    } catch (err) {
      console.error('Error fetching history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const addResep    = () => setResepList((p) => [...p, emptyResep()]);
  const removeResep = (id) => setResepList((p) => p.filter((r) => r.id !== id));
  const upResep     = (id, field, val) =>
    setResepList((p) => p.map((r) => r.id === id ? { ...r, [field]: val } : r));

  const handleSave = async () => {
    if (!form.keluhan_utama.trim()) { 
      Swal.fire({
        icon: 'warning',
        title: 'Perhatian',
        text: 'Keluhan utama wajib diisi.',
        confirmButtonColor: '#4F46E5'
      });
      return; 
    }

    setSaving(true);
    try {
      await axiosInstance.post('/dokter/rekam-medis', {
        pendaftaran_id,
        ...form,
        diagnosis: '',
        tindakan: [
          ...(form.tindakan_lain.trim() ? [form.tindakan_lain.trim()] : []),
        ],
        resep: resepList.filter((r) => r.nama_obat.trim()),
      });

      await Swal.fire({
        icon: 'success',
        title: 'Berhasil',
        text: 'Rekam medis berhasil disimpan. Status pasien: Selesai.',
        confirmButtonColor: '#4F46E5'
      });
      navigate('/dokter/antrian');
    } catch (e) {
      console.error(e);
      const msg = e.response?.data?.errors
        ? Object.values(e.response.data.errors).flat().join('\n')
        : e.response?.data?.message ?? 'Gagal menyimpan. Coba lagi.';
      
      Swal.fire({
        icon: 'error',
        title: 'Gagal Menyimpan',
        text: msg,
        confirmButtonColor: '#4F46E5'
      });
    } finally {
      setSaving(false);
    }
  };

  const parseResep = (str) => {
    try { return typeof str === 'string' ? JSON.parse(str) : (str ?? []); }
    catch { return []; }
  };

  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' }) : '-';

  const isReadOnly = !!existing;
  const pasien = pendaftaran?.pasien;
  const formattedId = formatPasienId(pasien?.id, pasien?.kode_pasien || pasien?.no_rekam_medis);

  const TABS = [
    { id: 'pemeriksaan', label: 'Pemeriksaan' },
    { id: 'tindakan',    label: 'Tindakan Medis' },
    { id: 'resep',       label: 'E-Resep' },
  ];

  if (loading) return (
    <DokterLayout title="Rekam Medis">
      <p style={{ padding: 48, color: '#9CA3AF', textAlign: 'center' }}>Memuat data…</p>
    </DokterLayout>
  );

  return (
    <DokterLayout title={isReadOnly ? 'Detail Rekam Medis' : 'Input Rekam Medis'}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
        .rm * { font-family: 'DM Sans', sans-serif; box-sizing: border-box; }

        .rm-banner {
          background: linear-gradient(120deg, #4F46E5 0%, #7C3AED 100%);
          border-radius: 16px; padding: 20px 24px; color: #fff;
          display: flex; justify-content: space-between; align-items: center;
          flex-wrap: wrap; gap: 14px; margin-bottom: 20px;
        }
        .rm-banner .pname { font-size: 20px; font-weight: 700; display: flex; align-items: center; gap: 10px; }
        .rm-banner .pmeta { font-size: 13px; opacity: .85; margin-top: 4px; }
        .id-badge-banner {
          background: rgba(255,255,255,0.2); color: #fff; padding: 2px 8px;
          border-radius: 6px; font-size: 12px; font-family: monospace; font-weight: 700;
        }
        .btn-history {
          background: #ffffff; color: #4F46E5; border: none; border-radius: 10px;
          padding: 8px 16px; font-size: 13px; font-weight: 700; cursor: pointer;
          transition: all .15s; display: inline-flex; align-items: center; gap: 6px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        .btn-history:hover { background: #EEF2FF; transform: translateY(-1px); }

        .ro-banner {
          background: #FEF3C7; border: 1px solid #FCD34D; border-radius: 10px;
          padding: 10px 16px; font-size: 13px; color: #92400E; font-weight: 600;
          margin-bottom: 16px;
        }

        .rm-tabs {
          display: flex; gap: 4px; padding: 4px; margin-bottom: 20px; background: #F3F4F6;
          border-radius: 12px;
        }
        .rm-tab {
          flex: 1; padding: 10px 6px; border-radius: 9px; border: none;
          background: none; cursor: pointer; font-size: 13px; font-weight: 600;
          color: #6B7280; transition: all .15s; font-family: inherit;
        }
        .rm-tab.active { background: #fff; color: #4F46E5; box-shadow: 0 1px 4px rgba(0,0,0,.1); }

        .rm-card {
          background: #fff; border: 1.5px solid #E5E7EB;
          border-radius: 14px; padding: 22px 24px; margin-bottom: 14px;
        }
        .rm-card h4 { margin: 0 0 16px; font-size: 15px; font-weight: 700; color: #374151; }

        .vitals-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 10px; }
        .vbox { border: 1.5px solid #E5E7EB; border-radius: 10px; padding: 12px 14px; }
        .vbox label {
          font-size: 10px; font-weight: 700; color: #9CA3AF;
          text-transform: uppercase; letter-spacing: .6px; display: block;
        }
        .vbox input {
          width: 100%; border: none; outline: none; font-size: 18px; font-weight: 700;
          color: #111827; background: transparent; font-family: inherit; margin-top: 4px;
        }
        .vbox input:disabled { color: #6B7280; }
        .vbox .unit { font-size: 11px; color: #9CA3AF; margin-top: 2px; }

        .fg { display: flex; flex-direction: column; gap: 5px; margin-bottom: 14px; }
        .fg:last-child { margin-bottom: 0; }
        .fg label { font-size: 13px; font-weight: 600; color: #374151; }
        .fg input, .fg select, .fg textarea {
          border: 1.5px solid #E5E7EB; border-radius: 8px; padding: 10px 12px;
          font-size: 14px; color: #111827; font-family: inherit; outline: none;
          transition: border-color .15s;
        }
        .fg input:focus, .fg select:focus, .fg textarea:focus {
          border-color: #6366F1; box-shadow: 0 0 0 3px #EEF2FF;
        }
        .fg input:disabled, .fg select:disabled, .fg textarea:disabled {
          background: #F9FAFB; color: #6B7280; cursor: default;
        }
        .fg textarea { resize: vertical; min-height: 90px; }

        .resep-card {
          position: relative; border: 1.5px solid #E5E7EB;
          border-radius: 12px; padding: 20px 20px 16px; margin-bottom: 12px;
        }
        .resep-num {
          position: absolute; top: -11px; left: 14px;
          background: #6366F1; color: #fff; border-radius: 10px;
          font-size: 11px; font-weight: 700; padding: 2px 10px;
        }
        .resep-del {
          position: absolute; top: 10px; right: 12px;
          background: none; border: none; cursor: pointer;
          color: #EF4444; font-size: 18px; line-height: 1; padding: 0;
        }
        .r-grid2 { display: grid; grid-template-columns: 2fr 1fr 1fr; gap: 10px; }
        .r-grid3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; margin-top: 10px; }

        .btn-add-resep {
          width: 100%; padding: 12px; border: 2px dashed #C7D2FE;
          border-radius: 11px; background: #F5F3FF; color: #6366F1;
          font-size: 13px; font-weight: 700; cursor: pointer; font-family: inherit;
          transition: all .15s;
        }
        .btn-add-resep:hover { background: #EEF2FF; border-color: #6366F1; }

        .rm-actions { display: flex; justify-content: flex-end; gap: 10px; padding-top: 8px; }
        .btn {
          padding: 11px 26px; border-radius: 9px; font-size: 14px; font-weight: 700;
          cursor: pointer; border: none; transition: all .15s; font-family: inherit;
        }
        .btn-cancel { background: #F3F4F6; color: #374151; }
        .btn-cancel:hover { background: #E5E7EB; }
        .btn-save   { background: #4F46E5; color: #fff; }
        .btn-save:hover   { background: #4338CA; }
        .btn-save:disabled { opacity: .5; cursor: not-allowed; }

        .modal-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,0.5);
          z-index: 200; display: flex; align-items: center; justify-content: center; padding: 16px;
        }
        .modal-box {
          background: #fff; border-radius: 16px; width: 100%; max-width: 800px;
          max-height: 85vh; overflow: hidden; display: flex; flex-direction: column;
          box-shadow: 0 20px 50px rgba(0,0,0,0.2);
        }
        .modal-header {
          padding: 16px 20px; border-bottom: 1px solid #E5E7EB; background: #F9FAFB;
          display: flex; justify-content: space-between; align-items: center;
        }
        .modal-body { display: flex; flex: 1; overflow: hidden; }
        .history-list {
          width: 250px; border-right: 1px solid #E5E7EB; background: #F9FAFB;
          padding: 12px; overflow-y: auto; flex-shrink: 0;
        }
        .history-item-btn {
          width: 100%; text-align: left; padding: 10px; border-radius: 8px;
          border: 1px solid #E5E7EB; background: #fff; margin-bottom: 8px;
          cursor: pointer; font-size: 12px; transition: all .15s;
        }
        .history-item-btn.active { border-color: #4F46E5; background: #EEF2FF; }
        .history-detail { flex: 1; padding: 20px; overflow-y: auto; }

        @media (max-width: 640px) {
          .vitals-grid, .r-grid2, .r-grid3 { grid-template-columns: 1fr; }
          .modal-body { flex-direction: column; }
          .history-list { width: 100%; border-right: none; border-bottom: 1px solid #E5E7EB; max-height: 150px; }
        }
      `}</style>

      <div className="rm" style={{ maxWidth: 880 }}>

        <div className="rm-banner">
          <div>
            <div className="pname">
              {pasien?.nama ?? '—'}
              <span className="id-badge-banner">{formattedId}</span>
            </div>
            <div className="pmeta">
              No. Antrian: <strong>{pendaftaran?.no_antrian ?? '-'}</strong> &nbsp;·&nbsp;
              Keluhan: {pendaftaran?.keluhan ?? existing?.keluhan_utama ?? '-'}
            </div>
          </div>
          <button className="btn-history" onClick={fetchPatientHistory}>
            📋 Riwayat RM Sebelumnya
          </button>
        </div>

        {isReadOnly && (
          <div className="ro-banner">
            ⚠️ Rekam medis untuk kunjungan ini sudah tersimpan — ditampilkan dalam mode baca saja.
          </div>
        )}

        <div className="rm-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              className={`rm-tab ${tab === t.id ? 'active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'pemeriksaan' && (
          <>
            <div className="rm-card">
              <h4>Tanda-Tanda Vital</h4>
              <div className="vitals-grid">
                {[
                  { key: 'tekanan_darah', label: 'Tekanan Darah', unit: 'mmHg' },
                  { key: 'nadi',          label: 'Nadi',          unit: 'bpm'  },
                  { key: 'suhu',          label: 'Suhu',          unit: '°C'   },
                ].map(({ key, label, unit }) => (
                  <div className="vbox" key={key}>
                    <label>{label}</label>
                    <input
                      value={form[key]}
                      placeholder={isReadOnly ? '-' : ''}
                      disabled={isReadOnly}
                      onChange={(e) => setForm((f) => ({
                        ...f,
                        [key]: formatVitalInput(key, e.target.value),
                      }))}
                    />
                    <div className="unit">{unit}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rm-card">
              <h4>Anamnesis &amp; Pemeriksaan</h4>
              <div className="fg">
                <label>Keluhan Utama *</label>
                <input
                  value={form.keluhan_utama}
                  disabled={isReadOnly}
                  placeholder="Keluhan utama pasien…"
                  onChange={(e) => setForm((f) => ({ ...f, keluhan_utama: e.target.value }))}
                />
              </div>
              <div className="fg">
                <label>Anamnesis</label>
                <textarea
                  value={form.anamnesis}
                  disabled={isReadOnly}
                  placeholder="Riwayat penyakit, riwayat keluarga, riwayat alergi…"
                  onChange={(e) => setForm((f) => ({ ...f, anamnesis: e.target.value }))}
                />
              </div>
              <div className="fg">
                <label>Pemeriksaan Fisik</label>
                <textarea
                  value={form.pemeriksaan_fisik}
                  disabled={isReadOnly}
                  placeholder="Hasil pemeriksaan fisik…"
                  onChange={(e) => setForm((f) => ({ ...f, pemeriksaan_fisik: e.target.value }))}
                />
              </div>
              <div className="fg">
                <label>Catatan Dokter</label>
                <textarea
                  value={form.catatan_dokter}
                  disabled={isReadOnly}
                  placeholder="Catatan tambahan dokter…"
                  onChange={(e) => setForm((f) => ({ ...f, catatan_dokter: e.target.value }))}
                />
              </div>
            </div>
          </>
        )}

        {tab === 'tindakan' && (
          <div className="rm-card">
            <h4>Tindakan Medis</h4>
            <div className="fg" style={{ marginBottom: 0 }}>
              <textarea
                value={form.tindakan_lain}
                disabled={isReadOnly}
                placeholder="Ketik tindakan medis yang diberikan…"
                onChange={(e) => setForm((f) => ({ ...f, tindakan_lain: e.target.value }))}
              />
            </div>
          </div>
        )}

        {tab === 'resep' && (
          <>
            {resepList.map((r, idx) => (
              <div className="resep-card" key={r.id}>
                <span className="resep-num">Obat #{idx + 1}</span>
                {!isReadOnly && resepList.length > 1 && (
                  <button className="resep-del" onClick={() => removeResep(r.id)}>×</button>
                )}

                <div className="r-grid2">
                  <div className="fg" style={{ marginBottom: 0 }}>
                    <label>Nama Obat</label>
                    <input
                      value={r.nama_obat}
                      disabled={isReadOnly}
                      placeholder="Nama obat / generik"
                      onChange={(e) => upResep(r.id, 'nama_obat', e.target.value)}
                    />
                  </div>
                  <div className="fg" style={{ marginBottom: 0 }}>
                    <label>Dosis</label>
                    <input
                      value={r.dosis}
                      disabled={isReadOnly}
                      placeholder="500"
                      onChange={(e) => upResep(r.id, 'dosis', e.target.value)}
                    />
                  </div>
                  <div className="fg" style={{ marginBottom: 0 }}>
                    <label>Satuan</label>
                    <select
                      value={r.satuan}
                      disabled={isReadOnly}
                      onChange={(e) => upResep(r.id, 'satuan', e.target.value)}
                    >
                      {SATUAN.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                </div>

                <div className="r-grid3">
                  <div className="fg" style={{ marginBottom: 0 }}>
                    <label>Frekuensi</label>
                    <select
                      value={r.frekuensi}
                      disabled={isReadOnly}
                      onChange={(e) => upResep(r.id, 'frekuensi', e.target.value)}
                    >
                      {FREKUENSI.map((f) => <option key={f}>{f}</option>)}
                    </select>
                  </div>
                  <div className="fg" style={{ marginBottom: 0 }}>
                    <label>Waktu Konsumsi</label>
                    <select
                      value={r.waktu}
                      disabled={isReadOnly}
                      onChange={(e) => upResep(r.id, 'waktu', e.target.value)}
                    >
                      {WAKTU.map((w) => <option key={w}>{w}</option>)}
                    </select>
                  </div>
                </div>

                <div className="fg" style={{ marginTop: 10, marginBottom: 0 }}>
                  <label>Catatan</label>
                  <input
                    value={r.catatan}
                    disabled={isReadOnly}
                    placeholder="Catatan khusus (opsional)"
                    onChange={(e) => upResep(r.id, 'catatan', e.target.value)}
                  />
                </div>
              </div>
            ))}

            {!isReadOnly && (
              <button className="btn-add-resep" onClick={addResep}>+ Tambah Obat</button>
            )}
          </>
        )}

        <div className="rm-actions" style={{ marginTop: 20 }}>
          <button className="btn btn-cancel" onClick={() => navigate('/dokter/antrian')}>
            {isReadOnly ? 'Kembali' : 'Batal'}
          </button>
          {!isReadOnly && (
            <button className="btn btn-save" onClick={handleSave} disabled={saving}>
              {saving ? 'Menyimpan…' : '💾 Simpan & Selesai'}
            </button>
          )}
        </div>

      </div>

      {showHistoryModal && (
        <div className="modal-overlay" onClick={() => setShowHistoryModal(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3 style={{ margin: 0, fontSize: 16, color: '#111827' }}>
                  Riwayat Rekam Medis Pasien
                </h3>
                <span style={{ fontSize: 12, color: '#6B7280' }}>
                  {pasien?.nama} ({formattedId})
                </span>
              </div>
              <button
                onClick={() => setShowHistoryModal(false)}
                style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#6B7280' }}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              {loadingHistory ? (
                <div style={{ padding: 40, textAlign: 'center', width: '100%', color: '#6B7280' }}>
                  ⏳ Memuat riwayat...
                </div>
              ) : patientHistory.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', width: '100%', color: '#9CA3AF' }}>
                  Belum ada riwayat kunjungan sebelumnya.
                </div>
              ) : (
                <>
                  <div className="history-list">
                    {patientHistory.map((item) => (
                      <button
                        key={item.id}
                        className={`history-item-btn ${selectedHistoryRm?.id === item.id ? 'active' : ''}`}
                        onClick={() => setSelectedHistoryRm(item)}
                      >
                        <div style={{ fontWeight: 700, color: '#111827' }}>
                          📅 {fmtDate(item.created_at || item.tanggal_kunjungan)}
                        </div>
                        <div style={{ color: '#6B7280', fontSize: 11, marginTop: 2 }}>
                          {item.keluhan_utama || 'Rekam Medis'}
                        </div>
                      </button>
                    ))}
                  </div>

                  <div className="history-detail">
                    {selectedHistoryRm ? (
                      <div>
                        <h4 style={{ margin: '0 0 12px', color: '#111827', borderBottom: '1px solid #E5E7EB', paddingBottom: 8 }}>
                          Detail Kunjungan ({fmtDate(selectedHistoryRm.created_at || selectedHistoryRm.tanggal_kunjungan)})
                        </h4>

                        <div style={{ marginBottom: 14 }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase' }}>Keluhan Utama</span>
                          <p style={{ margin: '2px 0 0', fontSize: 13, color: '#374151' }}>{selectedHistoryRm.keluhan_utama || '-'}</p>
                        </div>

                        {selectedHistoryRm.anamnesis && (
                          <div style={{ marginBottom: 14 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase' }}>Anamnesis</span>
                            <p style={{ margin: '2px 0 0', fontSize: 13, color: '#374151' }}>{selectedHistoryRm.anamnesis}</p>
                          </div>
                        )}

                        {selectedHistoryRm.tindakan && (
                          <div style={{ marginBottom: 14 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase' }}>Tindakan Medis</span>
                            <p style={{ margin: '2px 0 0', fontSize: 13, color: '#374151' }}>{selectedHistoryRm.tindakan}</p>
                          </div>
                        )}

                        <div>
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase' }}>Resep Obat</span>
                          <div style={{ marginTop: 4 }}>
                            {parseResep(selectedHistoryRm.resep).length > 0 ? (
                              parseResep(selectedHistoryRm.resep).map((o, i) => (
                                <div key={i} style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 6, padding: '6px 10px', fontSize: 12, marginBottom: 4 }}>
                                  <strong>{o.nama_obat}</strong> — {o.dosis} {o.satuan} ({o.frekuensi})
                                </div>
                              ))
                            ) : (
                              <p style={{ margin: '2px 0 0', fontSize: 13, color: '#374151' }}>-</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div style={{ color: '#9CA3AF', textAlign: 'center', marginTop: 40 }}>Pilih salah satu tanggal di sebelah kiri.</div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

    </DokterLayout>
  );
}